<template>
    <div>
        <b-alert :message="t('reports::message.realtime_data_info')" type="info"></b-alert>
        <b-datatable
            :key="datatableKey"
            ref="dataTable"
            :columns="columns"
            :endpoint="listRoute"
            :grid-title="gridTitle"
            :hidden-columns="hiddenColumns"
            :initial-filters="initialFilters"
            :keep-filters="true"
            :keep-filters-prefix="initialFilters.report_type"
            has-filter-selector
            @onReset="onReset"
        >
            <template #filters="{ filters }">
                <v-col class="pb-0" cols="3">
                    <b-filter-input v-model="filters.filters.name" :label="t('reports::label.name')"></b-filter-input>
                </v-col>
                <v-col class="pb-0" cols="3">
                    <b-filter-select
                        v-model="filters.filters.pharmacy_site_id"
                        :clearable="filters.filters.pharmacy_site_id !== null"
                        :items="pharmacySitesOptions"
                        :label="t('patients::grid.label.pharmacy_site')"
                        multiple
                    >
                    </b-filter-select>
                </v-col>
                <v-col class="pb-0" cols="3">
                    <b-filter-select
                        v-model="filters.filters.pharmacy_group_id"
                        :clearable="filters.filters.pharmacy_group_id !== null"
                        :items="pharmacyGroupsOptions"
                        :label="t('pharmacy-groups::form.label.pharmacy_group')"
                    >
                    </b-filter-select>
                </v-col>
                <v-col class="pb-0" cols="3">
                    <b-filter-select
                        v-model="filters.filters.consultant_id"
                        :clearable="filters.filters.consultant_id !== null"
                        :items="consultantsOptions"
                        :label="t('patients::grid.label.consultant')"
                        multiple
                    >
                    </b-filter-select>
                </v-col>
                <v-col class="pb-0" cols="3">
                    <b-filter-date
                        v-model="filters.filters.next_due_date"
                        :clearable="filters.filters.next_due_date !== null"
                        :label="t('patients::grid.label.next_due_date')"
                        range
                        :disabled="initialFilters.report_type === 'due_for_the_week'"
                    >
                    </b-filter-date>
                </v-col>
                <v-col class="pb-0" cols="3">
                    <b-filter-select
                        v-model="filters.filters.blood_testing_frequency"
                        :clearable="filters.filters.blood_testing_frequency !== null"
                        :items="filtersOptions.bloodTestingFrequency"
                        :label="t('patients::grid.label.blood_testing_frequency')"
                    >
                    </b-filter-select>
                </v-col>
                <v-col class="pb-0" cols="3">
                    <b-filter-date
                        v-model="filters.filters.frequency_change_date"
                        :clearable="filters.filters.frequency_change_date !== null"
                        :label="t('patients::grid.label.frequency_change_date')"
                        range
                    >
                    </b-filter-date>
                </v-col>
                <v-col v-if="initialFilters.report_type === 'all_patients'" class="pb-0" cols="3">
                    <b-filter-select
                        v-model="filters.filters.is_registered"
                        :items="filtersOptions.is_registered"
                        :label="t('patients::grid.label.is_registered')"
                        :clearable="filters.filters.is_registered !== null"
                    >
                    </b-filter-select>
                </v-col>
            </template>
            <template #buttons>
                <div class="px-2">
                    <v-tooltip top transition="v-fab-transition">
                        <template #activator="{ on: onTooltip, attrs: attrsTooltip }">
                            <v-menu offset-y>
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        color="primary"
                                        dark
                                        icon
                                        v-bind="{ ...attrs, ...attrsTooltip }"
                                        v-on="{ ...on, ...onTooltip }"
                                    >
                                        <v-icon>mdi-file-export-outline</v-icon>
                                    </v-btn>
                                </template>
                                <v-list dense>
                                    <v-list-item @click="exportRecord('csv')">
                                        <v-list-item-icon>
                                            <v-icon>mdi mdi-file-delimited</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title>{{ t('patients::button.export_csv') }}</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item @click="exportRecord('xlsx')">
                                        <v-list-item-icon>
                                            <v-icon>mdi mdi-file-excel</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title>{{ t('patients::button.export_xls') }}</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </template>
                        <span>{{ t('patients::button.export') }}</span>
                    </v-tooltip>
                </div>
            </template>

            <template #[`item.name`]="{ item }">
                <div
                    v-if="item.dms_id && can('patients.preview')"
                    class="link"
                    @click="$root.goTo(itemUrl(item))"
                >
                    {{ item.name }}
                </div>
                <div v-else>
                    {{ item.name }}
                </div>
            </template>

            <template #[`item.health_status`]="{ item }">
                <div v-if="healthStatus(item)" class="grid-status">
                    <span>
                        {{ healthStatus(item) }}
                    </span>
                </div>
            </template>

            <template #[`item.prohibited`]="{ item }">
                <div class="grid-status">
                    <span v-if="item.prohibited">
                        {{ t('patients::prohibited.true') }}
                    </span>
                    <span v-else>
                        {{ t('patients::prohibited.false') }}
                    </span>
                </div>
            </template>

            <template #[`item.max_dispensing`]="{ item }">
                <div class="grid-status">
                    <span
                        v-text="
                            item.max_dispensing ? t('patients::max_dispensing.yes') : t('patients::max_dispensing.no')
                        "
                    ></span>
                </div>
            </template>

            <template #[`item.has_treatment_break`]="{ item }">
                <div class="grid-status">
                    <span
                        v-text="
                            item.has_treatment_break
                                ? t('patients::treatment_break.true')
                                : t('patients::treatment_break.false')
                        "
                    ></span>
                </div>
            </template>

            <template #[`item.status`]="{ item }">
                {{ t('patients::status.' + item.status) }}
            </template>
            <template #[`item.gender`]="{ item }">
                <span class="text-capitalize">{{ item.gender }}</span>
            </template>
            <template #[`item.race`]="{ item }">
                <span class="text-capitalize">{{ item.race }}</span>
            </template>
            <template #[`item.birth_date`]="{ item }">
                {{ item.birth_date ? moment(item.birth_date).format($root.dateFormat) : t('default::label.na') }}
            </template>
            <template #[`item.diagnosis.diagnosis`]="{ item }">
                <span v-if="item.diagnosis.diagnosis">
                    {{ item.diagnosis.diagnosis }}
                </span>
                <span v-else>
                    {{ t('patients::previous_anti_psychotics.none') }}
                </span>
            </template>

            <template #[`item.health_information.past_history_of_neutropenia`]="{ item }">
                <div class="grid-status">
                    <span v-if="item.health_information.past_history_of_neutropenia">
                        {{ t('patients::past_history_of_neutropenia.true') }}
                    </span>
                    <span v-else>
                        {{ t('patients::past_history_of_neutropenia.false') }}
                    </span>
                </div>
            </template>
            <template #[`item.health_information.is_with_ben`]="{ item }">
                <div class="grid-status">
                    <span v-if="item.health_information.is_with_ben">
                        {{ t('patients::is_with_ben.true') }}
                    </span>
                    <span v-else>
                        {{ t('patients::is_with_ben.false') }}
                    </span>
                </div>
            </template>
            <template #[`item.schizoprenia_or_parkinson`]="{ item }">
                <div class="grid-status">
                    <span v-if="item.schizoprenia_or_parkinson">
                        {{ t('patients::schizophrenia_or_parkinson.true') }}
                    </span>
                    <span v-else>
                        {{ t('patients::schizophrenia_or_parkinson.false') }}
                    </span>
                </div>
            </template>
            <template #[`item.diagnosis.is_prescribed_clozapine`]="{ item }">
                <div class="grid-status">
                    <span v-if="item.diagnosis.is_prescribed_clozapine">
                        {{ t('patients::is_prescribed_clozapine.true') }}
                    </span>
                    <span v-else>
                        {{ t('patients::is_prescribed_clozapine.false') }}
                    </span>
                </div>
            </template>
            <template #[`item.diagnosis.previous_anti_psychotics`]="{ item }">
                <span v-if="item.diagnosis.previous_anti_psychotics">
                    {{ item.diagnosis.previous_anti_psychotics }}
                </span>
                <span v-else>
                    {{ t('patients::previous_anti_psychotics.none') }}
                </span>
            </template>
            <template #[`item.diagnosis.ward_name`]="{ item }">
                <span v-if="item.diagnosis.ward_name">
                    {{ item.diagnosis.ward_name }}
                </span>
                <span v-else>
                    {{ t('patients::previous_antipsychotics.none') }}
                </span>
            </template>
            <template #[`item.health_information.cnrd_date`]="{ item }">
                <span v-if="item.health_information.cnrd_date">
                    {{ item.health_information.cnrd_date }}
                </span>
                <span v-else>
                    {{ t('patients::previous_antipsychotics.none') }}
                </span>
            </template>
            <template #[`item.color`]="{ item }">
                <div v-if="item.color" :class="`blood-status ${item.color}--text`">
                    <div :class="`status ${item.color}--text`">
                        <v-icon>icon icon-blood-result</v-icon>
                    </div>
                    <span v-text="item.color"></span>
                </div>
                <div v-else>
                    <span>{{ t('default::label.na') }}</span>
                </div>
            </template>

            <template #[`item.validity_date`]="{ item }">
                {{ item.validity_date ? moment(item.validity_date).format($root.dateFormat) : t('default::label.na') }}
            </template>

            <template #[`item.is_registered`]="{ item }">
                <div class="grid-status">
                    <span>
                        {{ item.is_registered ? t('default::label.yes') : t('default::label.no') }}
                    </span>
                </div>
            </template>

            <template #[`item.blood_testing_frequency`]="{ item }">
                {{
                    item.blood_testing_frequency
                        ? t('patients::dispensing.form.frequency_items.' + item.blood_testing_frequency)
                        : t('default::label.na')
                }}
            </template>

            <template #[`item.next_due_date`]="{ item }">
                {{ item.next_due_date ? moment(item.next_due_date).format($root.dateFormat) : t('default::label.na') }}
            </template>

            <template #[`item.frequency_change_date`]="{ item }">
                {{
                    item.frequency_change_date
                        ? moment(item.frequency_change_date).format($root.dateFormat)
                        : t('default::label.na')
                }}
            </template>

            <template #[`item.pharmacy_site.pharmacy_groups.group_name`]="{ item }">
                <v-tooltip top transition="v-fab-transition">
                    <template #activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">
                            {{
                                item.pharmacy_site?.pharmacy_groups.length
                                    ? item.pharmacy_site.pharmacy_groups.map((entry) => entry.group_name).join(', ')
                                    : t('default::label.na')
                            }}
                        </span>
                    </template>
                    <span>
                        {{
                            item.pharmacy_site?.pharmacy_groups.length
                                ? item.pharmacy_site.pharmacy_groups.map((entry) => entry.group_name).join(', ')
                                : t('default::label.na')
                        }}
                    </span>
                </v-tooltip>
            </template>

            <template #[`item.actions`]="{ item }">
                <div class="table-actions">
                    <v-tooltip
                        v-if="can('api.v1.patients.store') && can('patients.create')"
                        top
                        transition="v-fab-transition"
                    >
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                @click="$root.goTo(itemUrl(item, true))"
                                v-on="on"
                            >
                                <v-icon>mdi-pencil</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('default::button.edit') }}</span>
                    </v-tooltip>

                    <v-tooltip
                        v-if="!can('api.v1.patients.update') && can('api.v1.patients.show')"
                        top
                        transition="v-fab-transition"
                    >
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                @click="$root.goTo(itemUrl(item))"
                                v-on="on"
                            >
                                <v-icon>mdi-eye</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('default::button.details') }}</span>
                    </v-tooltip>
                </div>
            </template>
        </b-datatable>
    </div>
</template>

<script>
import BAlert from '../../../../../../bgo/bgo-components/resources/js/components/BAlert.vue';
import BFilterDate from '../../../../../../bgo/bgo-components/resources/js/components/BFilterDate.vue';
import BFilterSelect from '../../../../../../bgo/bgo-components/resources/js/components/BFilterSelect.vue';
import _ from 'lodash';

export default {
    components: { BAlert, BFilterSelect, BFilterDate },
    props: {
        listRoute: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            datatableKey: 0,
            downloadingFile: false,
            hiddenColumns: [
                'id',
                'gender',
                'race',
                'birth_date',
                'max_dispensing',
                'health_status',
                'is_registered',
                'has_treatment_break',
                'prohibited',
                'diagnosis.diagnosis',
                'health_information.past_history_of_neutropenia',
                'health_information.is_with_ben',
                'schizoprenia_or_parkinson',
                'diagnosis.is_prescribed_clozapine',
                'diagnosis.previous_anti_psychotics',
                'diagnosis.ward_name',
                'health_information.cnrd_date',
                'collection_centre.name',
            ],
            columns: [
                {
                    text: t('reports::grid.label.id'),
                    value: 'id',
                    sortable: false,
                    width: 170,
                },
                {
                    text: t('patients::grid.label.blood_status'),
                    value: 'color',
                    sortable: true,
                    width: 120,
                },
                {
                    text: t('patients::grid.label.name'),
                    value: 'name',
                    sortable: true,
                    width: 200,
                },
                {
                    text: t('patients::grid.label.dms_id'),
                    value: 'dms_id',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('patients::grid.label.nhs_id'),
                    value: 'nhs_id',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('patients::grid.label.status'),
                    value: 'status',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('patients::grid.label.validity'),
                    value: 'validity_date',
                    sortable: true,
                    width: 120,
                },
                {
                    text: t('patients::grid.label.pharmacy_site'),
                    value: 'pharmacy_site.site_name',
                    sortable: true,
                    width: 200,
                },
                {
                    text: t('pharmacy-sites::label.pharmacy_groups'),
                    value: 'pharmacy_site.pharmacy_groups.group_name',
                    sortable: true,
                    width: 200,
                },
                {
                    text: t('patients::grid.label.consultant'),
                    value: 'consultant.name',
                    sortable: true,
                    width: 200,
                },
                {
                    text: t('patients::grid.label.blood_testing_frequency'),
                    value: 'blood_testing_frequency',
                    sortable: true,
                    width: 120,
                },
                {
                    text: t('patients::grid.label.next_due_date'),
                    value: 'next_due_date',
                    sortable: true,
                    width: 120,
                },
                {
                    text: t('patients::grid.label.frequency_change_date'),
                    value: 'frequency_change_date',
                    sortable: true,
                    width: 120,
                },
                {
                    text: t('default::grid.label.actions'),
                    value: 'actions',
                    sortable: false,
                    fields: null,
                    width: 100,
                    align: 'end',
                },
            ],
            filtersOptions: {
                treatmentBreakOptions: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('patients::treatment_break.true'),
                        value: 1,
                    },
                    {
                        text: t('patients::treatment_break.false'),
                        value: 0,
                    },
                ],
                prohibited: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('patients::prohibited.true'),
                        value: 1,
                    },
                    {
                        text: t('patients::prohibited.false'),
                        value: 0,
                    },
                ],
                cnrd_date: [
                    {
                        text: t('patients::options.cnrd_date.all'),
                        value: null,
                    },
                    {
                        text: t('patients::options.cnrd_date.empty_date'),
                        value: 0,
                    },
                    {
                        text: t('patients::options.cnrd_date.not_empty_date'),
                        value: 1,
                    },
                ],
                status: [],
                max_dispensing: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('patients::max_dispensing.yes'),
                        value: 1,
                    },
                    {
                        text: t('patients::max_dispensing.no'),
                        value: 0,
                    },
                ],
                validity: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('patients::validity.valid'),
                        value: 'valid',
                    },
                    {
                        text: t('patients::validity.invalid'),
                        value: 'invalid',
                    },
                ],
                health_status: [],
                is_registered: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('consultants::options.registered.yes'),
                        value: 1,
                    },
                    {
                        text: t('consultants::options.registered.no'),
                        value: 0,
                    },
                ],
                bloodTestingFrequency: [
                    {
                        text: 'Daily',
                        value: 'daily',
                    },
                    {
                        text: 'Weekly',
                        value: 'weekly',
                    },
                    {
                        text: 'Twice Weekly',
                        value: 'twice_weekly',
                    },
                    {
                        text: '2 Weeks',
                        value: '2_weekly',
                    },
                    {
                        text: '4 Weeks',
                        value: '4_weekly',
                    },
                ],
                pharmacySitesOptions: [],
                pharmacyGroupsOptions: [],
                consultantsOptions: [],
            },
        };
    },
    computed: {
        selectedPeriod() {
            return this.$store.state.reports.selectedPeriod;
        },
        canAccessRoute() {
            return this.can('patients.get');
        },
        gridTitle() {
            return this.$store.state.reports.gridTitle;
        },
        selectedPeriodMeta() {
            return this.$store.state.reports.reportChartData.meta.filters;
        },
        initialFilters() {
            return this.$store.state.reports.patientGrid.filters;
        },
        pharmacySitesOptions() {
            return this.$store.state.pharmacy_sites.pharmacySitesOptions.map((x) => ({ value: x.id, text: x.name }));
        },
        pharmacyGroupsOptions() {
            return this.$store.state.pharmacy_groups.pharmacyGroupsOptions.map((x) => ({ value: x.id, text: x.name }));
        },
        consultantsOptions() {
            return this.$store.state.consultants.consultantsOptions.map((x) => ({ value: x.id, text: x.name }));
        },
    },
    watch: {
        selectedPeriod(val) {
            if (this.$refs.dataTable) {
                this.$refs.dataTable.selectedPeriod = val;
                this.$refs.dataTable.applyFilters();
            }
        },
        initialFilters(filters) {
            this.datatableKey++;
            this.$refs.dataTable.filters = filters;
            this.$refs.dataTable.applyFilters();
            if (filters.report_type === 'all_patients') {
                this.appendColumns();
            } else {
                this.columns = this.$options.data().columns;
            }
        },
    },
    created() {
        this.$store.dispatch('pharmacy_sites/loadPharmacySitesOptions');
        this.$store.dispatch('pharmacy_groups/loadPharmacyGroupsOptions');
        this.$store.dispatch('consultants/loadConsultantsOptions');
        if (this.initialFilters.report_type === 'all_patients') {
            this.appendColumns();
        }
        axios.get(route('api.v1.patients.config')).then((response) => {
            this.filtersOptions.status = _.union(
                [
                    {
                        text: t('patients::status.all'),
                        value: null,
                    },
                ],
                response.data.statuses
            );

            this.filtersOptions.health_status = _.union(
                [
                    {
                        text: t('patients::status.all'),
                        value: null,
                    },
                ],
                response.data.health_statuses
            );
        });
    },
    methods: {
        async exportRecordAsPdf(item) {
            if (this.downloadingFile) {
                return;
            }

            this.downloadingFile = true;

            return await axios
                .get(route('patients.export-record-as-pdf', { patient: item.id }))
                .then((response) => {
                    const link = document.createElement('a');
                    link.href = response.request.responseURL;
                    document.body.appendChild(link);
                    link.click();
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => {
                    _.delay(() => {
                        this.downloadingFile = false;
                    }, 2000);
                });
        },
        itemUrl(item, edit = false) {
            return route(edit ? 'patients.get' : 'patients.preview', { id: item.id });
        },
        healthStatus(item) {
            return item.health_status;
        },
        onReset() {},
        async exportRecord(format) {
            if (!this.$refs.dataTable || this.$refs.dataTable.items.length === 0) {
                this.$root.notify('error', 'No records to export.');
                return;
            }

            const filters = this.$refs.dataTable.filters || {};
            const configHiddenColumns = this.$store.state.data_table.hiddenColumns;
            const visibleColumns = this.columns
                .filter((col) => !configHiddenColumns.includes(col.value))
                .map((col) => col.value)
                .filter(function (item) {
                    return item !== 'actions';
                });

            if (visibleColumns.length === 0) {
                this.$root.notify(
                    'error',
                    'No columns selected for export. Please select at least one column and try again.'
                );
                return;
            }
            const link = document.createElement('a');
            link.href = route('patients.export', {
                format,
                ...filters,
                fields: visibleColumns.join(','),
            });
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        appendColumns() {
            this.columns.splice(
                this.columns.length - 1,
                0,
                {
                    text: t('patients::grid.label.dob'),
                    value: 'birth_date',
                    sortable: true,
                    width: 120,
                },
                {
                    text: t('patients::grid.label.gender'),
                    value: 'gender',
                    sortable: true,
                    width: 100,
                },
                {
                    text: t('patients::grid.label.race'),
                    value: 'race',
                    sortable: true,
                    width: 130,
                },
                {
                    text: t('patients::grid.label.diagnosis'),
                    value: 'diagnosis.diagnosis',
                    sortable: true,
                    width: 170,
                },
                {
                    text: t('patients::grid.label.max_dispensing'),
                    value: 'max_dispensing',
                    sortable: true,
                    width: 120,
                },
                {
                    text: t('patients::grid.label.health_status'),
                    value: 'health_status',
                    sortable: true,
                    width: 200,
                },
                {
                    text: t('patients::grid.label.is_registered'),
                    value: 'is_registered',
                    sortable: true,
                    width: 120,
                },
                {
                    text: t('patients::grid.label.treatment_break'),
                    value: 'has_treatment_break',
                    sortable: true,
                    width: 120,
                },
                {
                    text: t('patients::grid.label.prohibited'),
                    value: 'prohibited',
                    sortable: true,
                    width: 120,
                },
                {
                    text: t('patients::grid.label.past_history_of_neutropenia'),
                    value: 'health_information.past_history_of_neutropenia',
                    sortable: true,
                    width: 100,
                },
                {
                    text: t('patients::grid.label.is_with_ben'),
                    value: 'health_information.is_with_ben',
                    sortable: true,
                    width: 100,
                },
                {
                    text: t('patients::grid.label.schizoprenia_or_parkinson'),
                    value: 'schizoprenia_or_parkinson',
                    sortable: true,
                    width: 100,
                },
                {
                    text: t('patients::grid.label.is_prescribed_clozapine'),
                    value: 'diagnosis.is_prescribed_clozapine',
                    sortable: true,
                    width: 100,
                },
                {
                    text: t('patients::grid.label.previous_anti_psychotics'),
                    value: 'diagnosis.previous_anti_psychotics',
                    sortable: true,
                    width: 170,
                },
                {
                    text: t('patients::grid.label.ward_name'),
                    value: 'diagnosis.ward_name',
                    sortable: true,
                    width: 170,
                },
                {
                    text: t('patients::grid.label.cnrd_date'),
                    value: 'health_information.cnrd_date',
                    sortable: true,
                    width: 120,
                },

                {
                    text: t('patients::grid.label.collection_centre'),
                    value: 'collection_centre.name',
                    sortable: true,
                    width: 220,
                }
            );
        },
    },
};
</script>

<style></style>
